import React from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import HelpFab from "@/components/HelpFab";
import WalletSweep from "@/components/wallets/WalletSweep";
import { Zap, ShieldCheck, MousePointerClick, Mail, Construction } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const CA = "#C2185B";
const GREEN = "#00863A";
const AMBER = "#B45309";

/* Its own page: a sweep is a sitting-down job, not something to trip over on the
   way to the wallet table. */

const STEPS = [
  {
    icon: MousePointerClick, colour: CA, title: "Two clicks per wallet",
    body: "Click the wallet in TokenPocket's own panel, then approve the signature. This page spots the switch by itself and carries on — no navigating, no pasting.",
  },
  {
    icon: ShieldCheck, colour: GREEN, title: "The signature is a login",
    body: "It proves you hold the wallet and opens its CaryPact session. It cannot move, stake or withdraw anything, and BotChain CP still has no way to spend from a wallet.",
  },
  {
    icon: Mail, colour: AMBER, title: "Wallets you don't hold",
    body: "Anything not in this TokenPocket can't be swept here — its owner has to tap their own sync link. Send those from the Wallets table.",
  },
];

export default function WalletSweepPage() {
  const { user } = useAuth();
  // Not tested end to end yet, so it carries a coming-soon face. The super admin
  // can still run it — he needs it to pull the BOT pledge and n/r figures — and
  // every other admin gets the notice without the tool.
  const canRun = !!user?.is_super_admin && !user?.is_demo;
  return (
    <div className="min-h-screen pb-10" data-testid="wallet-sweep-page">
      <Header />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-4">
        <div className="mb-4 fade-up">
          <div className="pill mb-2" data-testid="sweep-coming-soon-pill">
            <Construction size={12} className="mr-1" style={{ color: AMBER }} />Coming soon · still under test
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">Wallet sweep.</h1>
          <p className="text-ink-2 mt-1 text-sm max-w-3xl">
            <Zap size={13} className="inline align-[-2px] mr-1" style={{ color: CA }} />
            Go through the wallets in your TokenPocket one after another. Each one you connect has its own
            CaryPact income read, so the figures that show <b style={{ color: AMBER }}>n/r</b> on the dashboard —
            BOT mining, bought-hashrate revenue and BOT staking — become real numbers for that wallet.
          </p>
        </div>

        <div className="glass-strong p-4 mb-3 flex items-start gap-3" data-testid="sweep-under-construction"
          style={{ borderColor: "rgba(180,83,9,0.35)", background: "rgba(180,83,9,0.05)" }}>
          <Construction size={18} className="shrink-0 mt-0.5" style={{ color: AMBER }} />
          <div>
            <div className="text-[13px] font-semibold" style={{ color: AMBER }}>Under construction — not signed off yet</div>
            <p className="text-[12px] text-ink-3 leading-relaxed mt-0.5">
              The sweep runs a real CaryPact login for each wallet you approve, and we haven't finished testing it
              across a full set of wallets. Treat anything it imports as worth checking against CaryPact until this
              notice comes down.{canRun ? " You can still run it." : " It's switched off for now."}
            </p>
          </div>
        </div>

        {canRun && (
          <div className="grid md:grid-cols-3 gap-2 mb-2">
            {STEPS.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="glass-strong p-3.5" data-testid={`sweep-note-${s.title.split(" ")[0].toLowerCase()}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <Icon size={14} style={{ color: s.colour }} />
                    <span className="text-[13px] font-semibold">{s.title}</span>
                  </div>
                  <p className="text-[12px] text-ink-3 leading-relaxed">
                    {s.body}
                    {s.icon === Mail && (
                      <Link to="/wallets" className="ml-1 font-semibold" style={{ color: CA }} data-testid="sweep-to-wallets">
                        Open Wallets →
                      </Link>
                    )}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        {canRun ? <WalletSweep /> : (
          <div className="glass p-6 text-center" data-testid="sweep-locked">
            <p className="text-sm text-ink-2">
              This tool is being finished off. In the meantime, send a wallet owner their own sync link from the{" "}
              <Link to="/wallets" className="font-semibold" style={{ color: CA }} data-testid="sweep-locked-to-wallets">
                Wallets table
              </Link>.
            </p>
          </div>
        )}
      </div>
      <HelpFab />
    </div>
  );
}
